import React from 'react';
import Header from './Header';
import Footer from './Footer';
import './CookiePolicy.css';
import ReactMarkdown from 'react-markdown';
import BackToTopButton from './BackToTopButton';

import policyMeta from '../policyMeta.json';

const privacyPolicyDate = policyMeta.insertDate;
const privacyPolicyURL = policyMeta.insertWebsiteURL;
const privacyPolicyEmail = `[${policyMeta.supportEmail}](mailto:${policyMeta.supportEmail})`;

const privacyPolicyMarkdown = `
Welcome to Appstoria Inc., the company incorporated under the laws of Delaware, with its principal place of business at 4220 South Maryland Parkway, 2nd Floor, Las Vegas, Nevada, 89119, USA (“Company”, “we”, “us”, or “our”). This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you access or use our website [${privacyPolicyURL}](${privacyPolicyURL}) (the “Website”), our mobile applications (the “Apps”), and any related services (collectively, the “Services”).

By using our Services, you agree to the terms of this Privacy Policy, as well as our [Terms and Conditions](/terms-and-conditions) and [Cookie Policy](/cookie-policy). If you do not agree, please do not use our Services.

By accessing or using our Services, you confirm that you have read, understood, and accepted this Privacy Policy, and that you are at least 18 years old (or the minimum legal age in your jurisdiction). If you do not meet these criteria or do not consent to this policy, please refrain from using our Services and contact us at ${privacyPolicyEmail}.

### **1. INFORMATION WE COLLECT**

We collect and process various types of personal and non-personal information to provide, support, and improve our Apps and Services, including but not limited to:

**1.1 Personal Data You Provide Directly:**

* Identification details (e.g., full name, email address, phone number).
* Contact information (e.g., billing and mailing addresses).
* Transactional data (e.g., purchase history, license keys, payment details). Note: Full payment card data is securely processed by authorized third-party providers and is not stored by us.
* Account credentials (e.g., usernames, user IDs, passwords) required to access our Services.
* Support and communication records (e.g., inquiries, complaints, feedback).
* Limited usage data necessary for license compliance, technical support, or Service provisioning, varying by product or provider.

This information is used to fulfill orders, provide support, manage accounts, comply with legal obligations, communicate with you, and enhance our Services.

**1.2 Automatically Collected Information:**

When you interact with our Services, certain information is automatically collected via technology, including but not limited to:

* Device and technical details (e.g., IP address, browser type/version, operating system, language settings, device identifiers, location data where permitted, referring URLs).
* Usage data (e.g., pages visited, features used, session duration, downloads, searches).
* Diagnostic data (e.g., error reports, crash logs, system activity).

This information helps us ensure security, troubleshoot issues, improve performance, analyze usage, and enforce license compliance.

**1.3 Tracking Technologies:**

We may use cookies, web beacons, and similar technologies to collect usage information and enhance your experience. For details, please see our Cookie Policy.

**2. HOW AND WHY WE USE YOUR INFORMATION**

2.1. We collect and process your personal information in accordance with applicable data protection laws. This is done to operate, maintain, and enhance our Services, to communicate with you effectively, and to comply with legal and regulatory obligations. Your information may also be shared with trusted third parties where necessary for these purposes.

2.2. The purposes for which we process your information include, but are not limited to:

* Managing user accounts and providing access to our Services;
* Processing transactions, including orders and payments;
* Responding to inquiries and customer support requests;
* Sending administrative messages, service updates, and communications (including marketing messages, subject to your preferences);
* Collecting feedback and conducting surveys;
* Monitoring and improving the functionality, performance, and user experience of our Services;
* Detecting, preventing, and addressing fraud, security threats, and technical issues;
  Enforcing our Terms of Service and exercising or defending our legal rights.

**3. LEGAL BASES FOR PROCESSING YOUR INFORMATION**

3.1. We process your personal information only when necessary and with a valid legal basis under applicable laws. These bases may include your consent, compliance with legal obligations, performance of contractual duties, protection of your rights, or legitimate business interests.

3.2. Under the GDPR and other applicable laws, we may rely on the following legal bases to process your data:

* **Consent** (Article 6(1)(a) GDPR): We may process your data based on your explicit consent. You can withdraw your consent at any time.
* **Performance of a Contract** (Article 6(1)(b) GDPR): We process your data when necessary to fulfill our contractual obligations to you, or in response to pre-contractual requests (e.g., providing the services you have requested).
  **Legitimate Interests** (Article 6(1)(f) GDPR): We may process your data when necessary for our legitimate business interests, such as improving our services, marketing, diagnosing issues, or preventing fraud, provided that your rights and freedoms are not overridden.
  **Legal Obligations** (Article 6(1)(c) GDPR): We may process your data to comply with legal requirements, cooperate with law enforcement, protect our legal rights, or in connection with legal proceedings.

**4. WITH WHOM WE MAY SHARE YOUR PERSONAL DATA**

We do **not** sell your personal data. We may share your information with:

* **Service Providers:** Trusted third parties assisting with service delivery, support, analytics, and advertising, under strict confidentiality agreements.
* **Corporate Transactions:** In the event of mergers, acquisitions, or similar events, with notification where required.
* **Business Partners:** To provide relevant products, services, or promotions.
* **Law Enforcement and Legal Authorities:** When legally required or to protect rights and safety.

**5. HOW LONG DO WE KEEP YOUR PERSONAL DATA**

We retain the personal data of resellers only for as long as necessary to fulfill the purposes for which it was collected, including managing the business relationship, compliance with legal obligations, resolving disputes, and enforcing our agreements. After these purposes have been fulfilled, we will securely delete or anonymize the personal data, unless a longer retention period is required or permitted by applicable law.

**6. TRANSFER OF PERSONAL DATA**

Your personal information may be processed and stored in countries outside your country of residence, including countries that may not provide the same level of data protection. Where such transfers occur, we ensure adequate safeguards are in place (e.g., Standard Contractual Clauses, adequacy decisions, or your consent). By using our Services, you consent to the transfer and processing of your Personal Data in different jurisdictions as described in this Privacy Policy.

**7. SECURITY OF YOUR PERSONAL DATA**

We employ a range of appropriate technical and organizational safeguards to protect your personal data from unauthorized access, disclosure, alteration, or destruction. These safeguards include secure servers, encryption technologies, access restrictions, and internal data protection protocols designed to maintain the confidentiality and integrity of your information.

While we make every effort to implement commercially reasonable security measures, no method of transmission over the Internet or method of electronic storage is entirely secure. As such, we cannot guarantee absolute security. You are also responsible for keeping your account credentials confidential and for all activities conducted under your account.

**8. CHILDREN’S PRIVACY**

We do not knowingly collect or solicit personal information from children under the age of 13 in the United States (or the applicable minimum age in a given state), or under the age of 16 in the European Union. If we become aware that we have inadvertently collected personal data from a child below the relevant age threshold, we will take prompt steps to delete such information.

If you are a parent or legal guardian and believe that your child’s personal information has been provided to us in violation of applicable data protection laws, please contact us to request its deletion.

**9. YOUR PRIVACY RIGHTS**

Depending on your location, you may have certain rights regarding your personal data. These rights may include:

* The right to access the personal data we hold about you.
* The right to correct or update inaccurate or incomplete information.
  The right to request the deletion or erasure of your personal data, subject to certain legal exceptions.
* The right to restrict or object to the processing of your data in specific circumstances.
* The right to data portability, allowing you to obtain and reuse your personal data for your own purposes across different services.
* The right to withdraw consent at any time where processing is based on your consent (excluding services - related messages).

To exercise any of these rights, please contact us using the information provided in the “Contact Us” section. We will respond to your request in accordance with applicable data protection laws.

**10. PRIVACY RIGHTS FOR U.S. RESIDENTS**

10.1. **General:** If you reside in the United States, you may have certain rights under applicable state privacy laws, including but not limited to the California Consumer Privacy Act (CCPA) and the Virginia Consumer Data Protection Act (VCDPA). These rights can include:

* Access: The right to request details about the personal information we collect, use, and share.
* Deletion: The right to ask us to delete your personal information, subject to legal and operational exceptions.
* Opt-Out: The right to opt out of the sale or sharing of your personal information for targeted advertising or marketing purposes.
* Non-Discrimination: The right to receive equal service and pricing even if you exercise your privacy rights.
  Data Portability: The right to obtain a copy of your personal data in a portable format.

To exercise your rights, please contact us through the methods described in the “Contact Us” section. We will verify your identity and respond to your request in compliance with applicable laws.

10.2. **Do Not Sell My Personal Information:** We do not sell your personal information. If you are a California resident, you have the right under the California Consumer Privacy Act (CCPA) to opt out of the sale of your personal information. Since we do not sell personal information, this right does not currently apply.

10.3. **Do-Not-Track (DNT) Requests:** Some web browsers include a DNT feature that signals to websites that a user does not want their online activities tracked. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to or honor browser-initiated DNT signals.​​ We encourage you to manage your privacy preferences through your browser settings. However, we may update our policy to address this in the future.

**11. THIRD-PARTY SERVICES AND PLATFORMS**

Our Services may integrate with or link to third-party platforms (e.g., App Store). These platforms operate under their own privacy policies. We are not responsible for the privacy practices or content of such third parties. When you download or interact with our Apps through a third-party platform, their terms and policies govern any data processing that occurs on that platform.

**12. CHANGES TO THIS PRIVACY POLICY**

We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or for other operational, legal, or regulatory reasons. When we make changes, we will revise the “Last Updated” date at the top of the policy and, where appropriate, notify you by email or through a notice on our Website.

We encourage you to review this Privacy Policy periodically to stay informed about how we collect, use, and protect your personal data. Your continued use of our services after any updates constitutes your acceptance of the revised policy.

**13. CONTACT US**

If you have any comments or questions about this policy or our data protection practices, you can contact us by email: **${privacyPolicyEmail}.**
`;

const PrivacyPolicy = () => (
  <div className="page-outer cookie-policy-page">
    <Header />
    <main className="cookie-policy-main main-content">
      <div className="cookie-policy-header-bg">
        <div className="cookie-policy-header">
          <h1>Privacy Policy</h1>
          <div className="cookie-policy-date">Effective Date: {privacyPolicyDate}</div>
        </div>
      </div>
      <div className="cookie-policy-container">
        <div className="cookie-policy-inner">
          <ReactMarkdown>{privacyPolicyMarkdown}</ReactMarkdown>
        </div>
      </div>
    </main>
    <Footer />
    <BackToTopButton />
  </div>
);

export default PrivacyPolicy;
